/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-restricted-globals */
/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */

import {
  AfterViewInit, Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output,
} from '@angular/core';

import { Subscription } from 'rxjs';
import { ButtonSize, ButtonStyle } from 'src/aero/aero-button/aero-button.component';
import { CreditType } from 'src/app/image/image.component';
import { DisplayProps } from 'src/app/avatar/types';
import { AeroDialogService } from 'src/aero/aero-dialog/aero-dialog.service';
import { Resume } from '../resume.model';
import { ResumeService } from '../resume.service';
import { ResumeSocialProfileDialogComponent } from '../social-profile-dialog/social-profile-dialog.component';

@Component({
  selector: 'bulb-resume-contact-details',
  templateUrl: './contact-details.component.html',
  standalone: false,
})
export class ResumeContactDetailsComponent implements OnDestroy, OnInit, AfterViewInit {
  /**
   * Whether the current user can edit the contact details.
   */
  @Input() canEdit: boolean;

  /**
   * User's resume reference.
   */
  @Input() public resume: Resume;

  /**
   * Reference to the owner of the resume.
   */
  @Input() public user: any;

  /**
   *  Update the Resume
   */
  @Output() public updateResume: EventEmitter<Resume> = new EventEmitter();

  /**
   * Resume author profile image.
   */
  public profileImage: any;

  /**
   * Default avatar props of user.
   * Used to show default avatar if no user profile image is uploaded.
   */
  public avatarDisplayProps: DisplayProps;

  /**
   * value for containing div's width.
   */
  public divWidth: any;

  /**
   * Subscription of the profile image.
   */
  private profileImageSubscription: Subscription;

  /**
   * Button size for aero buttons
   */
  public buttonSize : ButtonSize = ButtonSize.LARGER;

  /**
   * Button style for aero buttons
   */
  public buttonStyle : ButtonStyle = ButtonStyle.OUTLINE;

  /**
   * Credit type of image from CreditType enum
   */
  public creditType : CreditType = CreditType.CIRCULAR;

  /**
   * reloads page if user rotates device to fix div width bug.
   */
  @HostListener('window:orientationchange', ['$event'])
  // eslint-disable-next-line class-methods-use-this
  onOrientationChange(event) {
    location.reload();
  }

  /**
   * Default Constructor.
   */
  constructor(
    private aeroDialogService : AeroDialogService,
    public resumeService : ResumeService,
  ) {
  }

  /**
   * A lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * To handle any additional initialization tasks.
   */
  ngOnInit() : void {
    this.updateProfileImage();
    this.avatarDisplayProps = this.resumeService.avatarDisplayProps;
    // this value has to be set here b/c assigning "fixed" will remove the
    // gridColumn value and shrink/grow the width of the div
    this.divWidth = $('.bulb-resume-contact-details').width();
    this.profileImageSizeHandler(this.divWidth);
  }

  /**
   * A lifecycle hook that is called after Angular has initialized child components.
   * To handle any additional initialization tasks.
   */
  ngAfterViewInit() {
    this.profileImageReveal();
  }

  /**
  * Method that sets the min and max width of the contact details div
  */
  // eslint-disable-next-line class-methods-use-this
  profileImageSizeHandler(divWidth: any) {
    $('.bulb-resume-contact-details').css({
      minWidth: divWidth,
      maxWidth: divWidth,
    });
  }

  /**
   * Method that reveals the profile image as the user scrolls.
   */
  profileImageReveal() {
    const setOpacity = ({ target, intersectionRatio }) => requestAnimationFrame(
      // eslint-disable-next-line no-param-reassign
      // eslint-disable-next-line no-return-assign
      () => (target.style.opacity = intersectionRatio),
    );
    const callback = (entries: { target: any; intersectionRatio: any; }[]) => {
      entries.forEach(setOpacity);
    };
    const root = document.querySelector('[data-obsvr-root]');
    const threshold = [0, 0.1, 0.25, 0.5, 0.75, 0.8, 0.9, 1];
    const options = { root, threshold };
    const observer = new IntersectionObserver(callback, options);
    const sections = document.querySelectorAll('[data-obsvr-fade]');
    sections.forEach((section) => {
      observer.observe(section);
    });
  }

  /**
   * Method that displays the resume contact information dialog.
   */
  openContactInfoModal() {
    const ref = this.aeroDialogService.showResumeContactInfoDialog(this.resume);
    ref.onClose.subscribe((resume: Resume) => {
      // update the resume
      if (resume) {
        this.resume = resume;
        this.updateResume.emit(resume);
      }
    });
  }

  /**
   * Method that displays the resume social profile dialog.
   *
   * @param resume for the user.
   */
  openSocialProfileDialog(resume: Resume) {
    const resumeSocialProfileDialogOptions = {
      width: '352px',
      closeOnEscape: true,
      dismissableMask: true,
      styleClass: 'bulb-resume-social-dialog aero-dialog',
      data: { resume },
    };
    const dialog = this.aeroDialogService.show(
      ResumeSocialProfileDialogComponent,
      resumeSocialProfileDialogOptions,
    );
    const subscription = dialog.onClose.subscribe((updates: Resume) => {
      subscription.unsubscribe();
      // update the resume
      if (updates) {
        this.resume = updates;
        this.updateResume.emit(updates);
      }
    });
  }

  /**
   * Update profile image.
   */
  updateProfileImage() {
    const divWidth = $('.bulb-resume-user-info-container').width();
    this.profileImage = this.user.circularImage;
    this.profileImageSubscription = this.resumeService.updateProfileImage
      .subscribe((profileImage: any) => {
        this.profileImage = profileImage;
        // handles the avatar image reveal on scroll
        if (this.profileImage) {
          this.profileImageReveal();
        }
      });
  }

  /**
   * A lifecycle hook that is called when a directive, pipe, or service is destroyed.
   * Use for any custom cleanup that needs to occur when the instance is destroyed.
   */
  ngOnDestroy() {
    if (this.profileImageSubscription) {
      this.profileImageSubscription.unsubscribe();
    }
  }
}
